<template>
  <div class="bsp-catalog-visibility" :aria-busy="phonesLoading || loading || saving">
    <p>Visibility is configured separately for each WhatsApp number. Hiding the catalog prevents catalog template messages from being sent from that number.</p>
    <p v-if="phonesLoading" role="status">Loading WhatsApp numbers…</p>
    <p v-else-if="phonesError" role="alert">{{ phonesError }}</p>
    <p v-else-if="!phones.length">No phone numbers are available for this WABA.</p>
    <template v-else>
      <ASelector v-model="selectedPhoneId" :items="phoneChoices" item-title="title" item-value="value"
        label="WhatsApp phone number" :disabled="saving || disabled" />
      <p v-if="loading" role="status">Checking catalog visibility in Meta…</p>
      <p v-else-if="error" role="alert">Could not confirm catalog visibility: {{ error }}. Refresh before making changes.</p>
      <p v-else-if="visible === null" role="status">Meta has not returned visibility settings for this number. Choose whether to show or hide the catalog; Meta must confirm the change after saving.</p>
      <ATag v-else-if="visible !== undefined" :text="visible ? 'Visible on this number' : 'Hidden on this number'"
        :color="visible ? 'success' : 'warning'" type="secondary" />
      <div v-if="visible === null" class="bsp-component-actions">
        <AButton size="small" :loading="saving"
          :disabled="!canManage || disabled || loading || saving" @click="setVisibility(true)">Show catalog</AButton>
        <AButton size="small" variant="outlined" :loading="saving"
          :disabled="!canManage || disabled || loading || saving" @click="setVisibility(false)">Hide catalog</AButton>
      </div>
      <ASwitch v-else :model-value="visible === true" label="Show catalog in WhatsApp on this number"
        :loading="saving" :disabled="!canManage || disabled || loading || saving || visible === undefined"
        @update:model-value="setVisibility" />
      <p v-if="!canManage">Only BSP administrators can change catalog visibility.</p>
      <p v-if="saved" role="status">Catalog visibility confirmed in Meta.</p>
    </template>
    <AButton size="small" variant="outlined" prepend-icon="tabler:refresh"
      :disabled="loading || saving || phonesLoading || disabled" @click="refreshSettings">Refresh visibility</AButton>
  </div>
</template>

<script setup lang="ts">
import { computed, onScopeDispose, ref, watch } from 'vue'
import { AButton, ASelector, ASwitch, ATag } from '@adereso-ai/vue-components'
import AccountService from '@/services/AccountService/AccountService'
import MetaService from '@/services/MetaService/MetaService'
import type { PhoneNumber } from '@/services/AccountService/types'
import { useCatalogVisibility } from '@/composables/useCatalogVisibility'

const props = withDefaults(defineProps<{
  wabaId: string
  canManage: boolean
  disabled?: boolean
  initialPhoneId?: string
}>(), { disabled: false, initialPhoneId: '' })
const emit = defineEmits<{(event: 'update:phoneId', id: string): void}>()
const selectedPhoneId = ref('')
const phones = ref<PhoneNumber[]>([])
const phonesLoading = ref(false)
const phonesError = ref('')
const saved = ref(false)
let phoneGeneration = 0
const phoneChoices = computed(() => phones.value.map(phone => ({
  title: `${phone.display_phone_number} · ${phone.verified_name}`, value: phone.id
})))
const { visible, loading, saving, error, refresh, save } = useCatalogVisibility(
  computed(() => props.wabaId), selectedPhoneId, MetaService.getInstance
)

async function loadPhones () {
  const request = ++phoneGeneration
  selectedPhoneId.value = ''
  phones.value = []
  phonesError.value = ''
  phonesLoading.value = true
  try {
    const wabaId = props.wabaId
    const result = await (await AccountService.getInstance()).getWhatsappBusinessAccountPhoneNumbers(wabaId)
    if (request !== phoneGeneration) return
    const meta = await MetaService.getInstance()
    // Local records can outlive their Meta phone asset. Only offer numbers for
    // which Meta can actually read catalog visibility; never guess from order.
    const checks = await Promise.allSettled(result.map(phone => meta.getCommerceSettings(wabaId, phone.id)))
    if (request !== phoneGeneration) return
    phones.value = result.filter((phone, index) => checks[index].status === 'fulfilled')
    if (result.length && !phones.value.length) {
      throw new Error('Meta could not confirm catalog access for any phone number. Refresh to try again.')
    }
    selectedPhoneId.value = phones.value.some(phone => phone.id === props.initialPhoneId)
      ? props.initialPhoneId
      : phones.value[0]?.id || ''
  } catch (e) {
    if (request === phoneGeneration) phonesError.value = e instanceof Error ? e.message : String(e)
  } finally {
    if (request === phoneGeneration) phonesLoading.value = false
  }
}

async function setVisibility (value: unknown) {
  if (typeof value !== 'boolean' || !props.canManage || props.disabled) return
  saved.value = false
  saved.value = await save(value)
}

async function refreshSettings () {
  saved.value = false
  if (phonesError.value || !phones.value.length) await loadPhones()
  else await refresh()
}

watch(() => props.wabaId, loadPhones, { immediate: true })
watch(selectedPhoneId, id => {
  saved.value = false
  if (id) emit('update:phoneId', id)
})
watch(() => props.initialPhoneId, id => {
  if (phones.value.some(phone => phone.id === id)) selectedPhoneId.value = id
})
onScopeDispose(() => { phoneGeneration++ })
</script>

<style scoped>
.bsp-catalog-visibility { display: grid; gap: 16px; }
.bsp-catalog-visibility p { margin: 0; overflow-wrap: anywhere; }
</style>
